import cloneDeep from "lodash.clonedeep";
import uniqBy from "lodash.uniqby";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  GetProcessesResponseBodyType,
  ProcessStatusTableDto,
  WebsocketData,
  WebsocketEntityModificationMessage,
  WebsocketProcessMessage,
} from "@intelligems/ig-types";
import { useDialogs } from "@src/hooks/dialog";
import { useDiscountSyncErrorToast } from "@src/hooks/toast";
import { useDispatch } from "@src/store";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { useGetOrg } from "../api/organizations/get-organization";
import { getExperience } from "../api/experiences/get-experience";
import {
  experienceKeys,
  historyKeys,
  organizationsKeys,
  personalizationKeys,
  processesKeys,
  setExperienceQueryData,
} from "../api/queryKeys";
import { useWebsocket } from "../hooks/websocket";
import {
  getIgnoreUpdateDetected,
  sendWebsocketMessage,
  VoidDialogProps,
} from "../store/slices/global";
import {
  initExperimentFromDto,
  selectedExperience,
} from "../store/slices/experiences";
import { useEditPageExperienceId, useIsEditPage } from "../utils/constants";
import { handleBillingCommandUpdate } from "./handleBillingCommandUpdate";
import { handleShippingProviderPublishProgress } from "./handleShippingProviderPublishProgress";
import { updateEntityModificationCache } from "./updateEntityModificationCache";

export const WebsocketProvider = () => {
  const organization = useGetOrg();
  const websocket = useWebsocket();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isEditPage = useIsEditPage();
  const editPageExperienceId = useEditPageExperienceId();
  const discountSyncErrorToast = useDiscountSyncErrorToast();

  const ignoreUpdateDetected = useSelector(getIgnoreUpdateDetected);
  const currentExperience = useSelector(selectedExperience);
  const { openDialog, closeDialog, isDialogOpen } = useDialogs();

  const isUpdateDetectedDialogOpen = isDialogOpen("updatedDetected");
  const isRedirectToPluginDialogOpen = isDialogOpen("redirectToPlugin");

  useEffect(() => {
    if (
      !isEditPage &&
      isUpdateDetectedDialogOpen &&
      !isRedirectToPluginDialogOpen
    ) {
      closeDialog("updatedDetected");
    }
  }, [
    isEditPage,
    isUpdateDetectedDialogOpen,
    isRedirectToPluginDialogOpen,
    closeDialog,
  ]);

  const message = websocket.lastJsonMessage;
  const orgId = organization.data.org.id;

  useEffect(() => {
    if (message) {
      handleWebsocketMessage(message, {
        currentExperience,
        dispatch,
        editPageExperienceId,
        discountSyncErrorToast,
        ignoreUpdateDetected,
        isDialogOpen,
        isEditPage,
        openDialog,
        orgId,
        queryClient,
      });
    }
  }, [message, orgId]);

  return null;
};

export type WebsocketMessageHandlerContext = {
  currentExperience: ReturnType<typeof selectedExperience>;
  dispatch: ReturnType<typeof useDispatch>;
  editPageExperienceId: ReturnType<typeof useEditPageExperienceId>;
  discountSyncErrorToast: ReturnType<typeof useDiscountSyncErrorToast>;
  ignoreUpdateDetected: boolean;
  isDialogOpen: ReturnType<typeof useDialogs>["isDialogOpen"];
  isEditPage: boolean;
  openDialog: ReturnType<typeof useDialogs>["openDialog"];
  orgId: string;
  queryClient: QueryClient;
};

function handleWebsocketMessage(
  message: WebsocketData,
  context: WebsocketMessageHandlerContext
) {
  const { dispatch, discountSyncErrorToast, orgId, queryClient } = context;

  dispatch(sendWebsocketMessage(message));

  if (message.type === "configUpdate") {
    void queryClient.invalidateQueries({
      queryKey: historyKeys.allConfigEvents,
    });
  } else if (message.type === "invalidateOrgSummary") {
    void queryClient.invalidateQueries({
      queryKey: organizationsKeys.orgSummary(orgId),
    });
    void queryClient.invalidateQueries({
      queryKey: [{ scope: "admin", agencies: true, type: "singleAgency" }],
    });
    void queryClient.invalidateQueries({ queryKey: organizationsKeys.one });
  } else if (message.type === "billingCommandUpdate") {
    handleBillingCommandUpdate({
      message,
      orgId,
      queryClient,
      notifyError: toast.error,
    });
  } else if (message.type === "processUpdate") {
    handleProcessUpdate(message, context);
  } else if (message.type === "entityModification") {
    handleEntityModification(message, context);
  } else if (message.type === "syncPricingFunctionComplete") {
    handleSyncPricingFunctionComplete({ orgId, queryClient });
  } else if (message.type === "syncDiscountFunctionStatusChange") {
    discountSyncErrorToast(message.status);
  } else if (message.type === "shippingProviderPublishProgress") {
    handleShippingProviderPublishProgress(message, queryClient);
  }
}

function handleProcessUpdate(
  message: WebsocketProcessMessage,
  context: WebsocketMessageHandlerContext
) {
  const { orgId, queryClient } = context;
  if (message.action === "start") {
    handleProcessUserError(message, context);
    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(orgId),
      (data) => getProcessesAfterStart(data, message)
    );
  } else if (message.action === "update") {
    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(orgId),
      (data) => getProcessesAfterUpdate(data, message)
    );
  } else {
    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(orgId),
      (data) => getProcessesAfterEnd(data, message)
    );
  }
}

function handleProcessUserError(
  message: WebsocketProcessMessage,
  context: WebsocketMessageHandlerContext
) {
  const process = message.process as ProcessStatusTableDto;
  if (process.type !== "userError" || !process?.dialogKey) return;

  const { currentExperience, dispatch, openDialog, queryClient } = context;
  openDialog(process.dialogKey as keyof VoidDialogProps);
  void queryClient.invalidateQueries({
    queryKey: experienceKeys.experimentList,
  });
  if (process.experienceId) {
    void getExperience(process.experienceId).then((data) => {
      if (data.experience) {
        setExperienceQueryData(queryClient, data.experience);
        if (currentExperience?.id === data.experience.id) {
          dispatch(
            initExperimentFromDto({
              experience: data.experience,
              testFlowType: data.experience.type,
            })
          );
        }
      }
    });
  }
}

function getProcessesAfterStart(
  data: GetProcessesResponseBodyType | undefined,
  message: WebsocketProcessMessage
) {
  return {
    processes: uniqBy(
      [message.process as ProcessStatusTableDto, ...(data?.processes || [])],
      (_) => _.type
    ),
  };
}

function getProcessesAfterUpdate(
  data: GetProcessesResponseBodyType | undefined,
  message: WebsocketProcessMessage
) {
  const index = findProcessIndex(data, message);
  if (index === -1) return data;

  const processes: ProcessStatusTableDto[] = cloneDeep(data!.processes);
  if ("loadingPercent" in message.process) {
    if (
      message.process.loadingPercent &&
      message.process.loadingPercent >= (processes[index].loadingPercent || 0)
    ) {
      processes[index] = message.process;
    } else if (message.process.loadingPercent === null) {
      processes[index] = message.process;
    }
  } else {
    processes[index] = message.process as ProcessStatusTableDto;
  }

  return { processes };
}

function getProcessesAfterEnd(
  data: GetProcessesResponseBodyType | undefined,
  message: WebsocketProcessMessage
) {
  const index = findProcessIndex(data, message);
  if (index === -1) return data;

  return {
    processes: data!.processes.toSpliced(index, 1),
  };
}

export function handleEntityModification(
  message: WebsocketEntityModificationMessage,
  context: WebsocketMessageHandlerContext
) {
  const isCurrentEditPageExperience =
    message.experienceId === context.editPageExperienceId;

  if (
    isCurrentEditPageExperience &&
    shouldCheckEditPageUpdateDetection(message, context)
  ) {
    setTimeout(() => {
      context.openDialog("updatedDetected");
    }, 0);
  } else {
    updateEntityModificationCache(message, context.queryClient);
  }

  invalidateEntityModificationLists(message, context.queryClient);

  if (message.message) {
    toast.success(message.message);
  }
}

function shouldCheckEditPageUpdateDetection(
  message: WebsocketEntityModificationMessage,
  context: WebsocketMessageHandlerContext
) {
  return (
    context.isEditPage &&
    !context.isDialogOpen("redirectToPlugin") &&
    !context.ignoreUpdateDetected &&
    !message.testProducts
  );
}

function invalidateEntityModificationLists(
  message: WebsocketEntityModificationMessage,
  queryClient: QueryClient
) {
  if (message.experienceId) {
    void queryClient.invalidateQueries({
      queryKey: experienceKeys.experimentList,
    });
  } else if (message.campaignId) {
    void queryClient.invalidateQueries({
      queryKey: experienceKeys.homeOverviewPersonalizationList,
    });
    void queryClient.invalidateQueries({
      queryKey: personalizationKeys.all(true),
    });
    void queryClient.invalidateQueries({
      queryKey: personalizationKeys.all(false),
    });
  }
}

function handleSyncPricingFunctionComplete({
  orgId,
  queryClient,
}: {
  orgId: string;
  queryClient: QueryClient;
}) {
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.cartTransforms(orgId),
  });
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.metafield(orgId),
  });
  queryClient.setQueryData<GetProcessesResponseBodyType>(
    processesKeys.one(orgId),
    (data) => {
      return {
        processes: (data?.processes ?? []).filter(
          (process) => process.type !== "syncPricingFunction"
        ),
      };
    }
  );
}

function findProcessIndex(
  data: { processes?: ProcessStatusTableDto[] } | undefined,
  message: WebsocketProcessMessage
) {
  if (!data?.processes) return -1;
  if (message.process.id) {
    return data.processes.findIndex(
      (process) => process.id === message.process.id
    );
  } else {
    return data.processes.findIndex(
      (process) => process.type === message.process.type
    );
  }
}
